import { TFunction } from "i18next";
import { DateRangePicker } from "rsuite";
import { differenceInDays, startOfMonth, subDays, subYears } from "date-fns";

import type { IPreDefinedRange } from "libs/design-system/src/types/date_range_picker";

import { getLang } from "./localStorageUtils";
import { formatToISODate } from "libs/design-system/src/utils/dateFormats";


export const TODAY = new Date();
export const YESTERDAY = subDays(TODAY, 1);

export const MIN_DATE = subYears(YESTERDAY, 2);


const { combine, allowedDays, after, before, allowedRange } = DateRangePicker;

export const customLocales = {
  formattedMonthPattern: "MMMM yyyy"
};

export function formatDateRange(date: Date, yearFormat?: 'short' | 'long') {

  const yearOption = yearFormat === 'short' ?
    { year: '2-digit' }
    :
    (
      yearFormat === 'long' ?
        { year: 'numeric' }
        :
        {}
    )

  const options: any = { month: 'short', day: 'numeric', ...yearOption };
  const lang = getLang();

  const parsedDate = new Date(date);
  return parsedDate.toLocaleDateString(lang, options);
};

export const getDaysBetween = (startDate: Date, endDate: Date) => {
  return Math.abs(differenceInDays(startDate, endDate)) + 1;
};

export const getDisabledDates = (startDate: Date, endDate: Date, disabledAfter?: 'start' | 'end') => {
  return combine(
    combine(
      allowedDays(getDaysBetween(startDate, endDate)),
      after(disabledAfter === 'start' ? startDate : endDate),
    ),
    before(MIN_DATE)
  )
};

export const getComparedDates = (startDate: Date, endDate: Date) => {
  const daysBetween = getDaysBetween(endDate, startDate);

  return {
    compareStartDate: subDays(startDate, daysBetween),
    compareEndDate: subDays(endDate, daysBetween)
  };
};

export const getDefaultDates = () => {
  const startDate = startOfMonth(TODAY);
  const endDate = TODAY;

  return {
    startDate,
    endDate,
    ...getComparedDates(startDate, endDate)
  };
};
const {
  startDate: defaultMainStartDate,
  endDate: defaultMainEndDate,
  compareStartDate: defaultCompareStartDate,
  compareEndDate: defaultCompareEndDate
} = getDefaultDates();
export const mainPreDefinedRanges: (t: TFunction) => IPreDefinedRange[] = (t) => ([
  {
    index: 1,
    name: 'today',
    disabled: false,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.today`),
    selectionRange: [TODAY, TODAY],
    disabledRange: allowedRange(TODAY, TODAY),
  },
  {
    index: 2,
    name: 'last7Days',
    disabled: false,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.last7Days`),
    selectionRange: [subDays(TODAY, 7), subDays(TODAY, 1)],
    disabledRange: allowedRange(subDays(TODAY, 7), subDays(TODAY, 1)),
  },
  {
    index: 3,
    name: 'last30Days',
    disabled: false,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.last30Days`),
    selectionRange: [subDays(TODAY, 29), TODAY],
    disabledRange: allowedRange(subDays(TODAY, 29), TODAY),
    default: true,
  },
  {
    index: 4,
    name: 'last90Days',
    disabled: false,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.last90Days`),
    selectionRange: [subDays(TODAY, 90), subDays(TODAY, 1)],
    disabledRange: allowedRange(subDays(TODAY, 90), subDays(TODAY, 1)),
  },
  {
    index: 5,
    name: 'lastYear',
    disabled: false,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.lastYear`),
    selectionRange: [subDays(TODAY, 365), subDays(TODAY, 1)],
    disabledRange: allowedRange(subDays(TODAY, 365), subDays(TODAY, 1)),
  },
  {
    index: 6,
    name: 'custom',
    disabled: false,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.custom`),
    selectionRange: [defaultMainStartDate, defaultMainEndDate],
    disabledRange: allowedRange(MIN_DATE, TODAY),
  },
]);

export const comparePreDefinedRanges: (t: TFunction) => IPreDefinedRange[] = (t) => ([
  {
    index: 1,
    name: 'yesterday',
    disabled: true,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.yesterday`),
    selectionRange: [subDays(TODAY, 1), subDays(TODAY, 1)],
    disabledRange: () => true
  },
  {
    index: 2,
    name: 'prev7Days',
    disabled: true,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.prev7Days`),
    selectionRange: [subDays(TODAY, 14), subDays(TODAY, 8)],
    disabledRange: () => true
  },
  {
    index: 3,
    name: 'prev30Days',
    disabled: true,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.prev30Days`),
    selectionRange: [subDays(TODAY, 59), subDays(TODAY, 30)],
    disabledRange: () => true,
    default: true,
  },
  {
    index: 4,
    name: 'prev90Days',
    disabled: true,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.prev90Days`),
    selectionRange: [subDays(TODAY, 180), subDays(TODAY, 91)],
    disabledRange: () => true
  },
  {
    index: 5,
    name: 'prevYear',
    disabled: true,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.prevYear`),
    selectionRange: [subDays(TODAY, 730), subDays(TODAY, 366)],
    disabledRange: () => true
  },
  {
    index: 6,
    name: 'custom',
    disabled: true,
    label: t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.custom`),
    selectionRange: [defaultCompareStartDate, defaultCompareEndDate],
    disabledRange: getDisabledDates(defaultCompareStartDate, defaultCompareEndDate),
  },
]);

export const getPickerBtnText = (t: TFunction, startDate: Date, endDate: Date, rangeName?: string, shouldSelectOneDate = false) => {

  const isYearlyRangeSelected = rangeName?.toLowerCase().includes('year');

  const currentYear = new Date().getFullYear();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  const yearOption = (
    shouldSelectOneDate ||
    isYearlyRangeSelected ||
    startYear !== currentYear ||
    endYear !== currentYear
  ) ?
    'long'
    :
    undefined;

  return (
    `${rangeName ? `${t(`innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.${rangeName}`)},` : ''}
     ${formatDateRange(startDate, yearOption)}
     ${shouldSelectOneDate ? '' : `~ ${formatDateRange(endDate, yearOption)}`}
    `
  );
};

export const isoFormattedDateRange = (range: [Date, Date]) => {
  const [formattedStartDate, formattedEndDate] = (range as [Date, Date])?.map((date) => (formatToISODate(date)));

  return { start_date: formattedStartDate, end_date: formattedEndDate };
};